import { lazy, Suspense, type ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
} from "react-router";

import { AppProviders } from "@/components/providers/app-providers";
import { AuthenticatedLayout } from "@/layouts/authenticated-layout";
import { WorkspaceShellLayout } from "@/layouts/workspace-shell-layout";
import { ContentSlotPending } from "@/components/ui/content-slot-pending";
import { toStaticExportHref } from "@/config/normalize-application-pathname";
import { useAppI18n } from "@/i18n/context";

/** Root layout: AppProviders must sit inside the Router (useNavigate / useLocation). */
function RootLayout() {
  return (
    <AppProviders>
      <Outlet />
    </AppProviders>
  );
}

const workspaceHomeImport = import("@/pages/workspace/home");
/** 启动即下载，与 /v1/me 并行，避免会话就绪后才开始首页分包。 */
const WorkspaceHomePage = lazy(() => workspaceHomeImport);
const DashboardRedirectPage = lazy(
  () => import("@/pages/dashboard-redirect"),
);
const SettingsPage = lazy(() => import("@/pages/settings"));
const OnboardingPage = lazy(() => import("@/pages/onboarding"));

const CampaignsPage = lazy(() => import("@/pages/workspace/campaigns"));
const AdvertiserAccountsPage = lazy(
  () => import("@/pages/workspace/advertiser-accounts"),
);
const StoresPage = lazy(() => import("@/pages/workspace/stores"));
const CreativeAssetsPage = lazy(
  () => import("@/pages/workspace/creative-assets"),
);
const BusinessCentersPage = lazy(
  () => import("@/pages/workspace/business-centers"),
);
const IntegrationsPage = lazy(
  () => import("@/pages/workspace/integrations"),
);
const ReferralsPage = lazy(() => import("@/pages/workspace/referrals"));
const MembersPage = lazy(() => import("@/pages/workspace/members"));
const RolesPage = lazy(() => import("@/pages/workspace/roles"));
const AuditEventsPage = lazy(
  () => import("@/pages/workspace/audit-events"),
);
const ProfitCalculatorPage = lazy(
  () => import("@/pages/workspace/profit-calculator"),
);
const RoiCalculatorPage = lazy(
  () => import("@/pages/workspace/roi-calculator"),
);
const AudiencesPage = lazy(() => import("@/pages/workspace/audiences"));
const PausedPage = lazy(() => import("@/pages/workspace/paused"));

const AdminUsersPage = lazy(() => import("@/pages/admin/users"));
const AdminWorkspacesPage = lazy(
  () => import("@/pages/admin/workspaces"),
);
const AdminAuditEventsPage = lazy(
  () => import("@/pages/admin/audit-events"),
);

const LoginPage = lazy(() => import("@/pages/auth/login"));
const RegisterPage = lazy(() => import("@/pages/auth/register"));
const ForgotPasswordPage = lazy(
  () => import("@/pages/auth/forgot-password"),
);
const UpdatePasswordPage = lazy(
  () => import("@/pages/auth/update-password"),
);
const ConfirmPage = lazy(() => import("@/pages/auth/confirm"));
const AuthCallbackPage = lazy(() => import("@/pages/auth/callback"));
const InvitationPage = lazy(() => import("@/pages/auth/invitation"));
const TikTokOAuthCallbackPage = lazy(
  () => import("@/pages/oauth/tiktok-callback"),
);

function SuspensePage({ children }: { children: ReactNode }) {
  const { t } = useAppI18n();
  return (
    <Suspense
      fallback={<ContentSlotPending label={t("common.tableLoading")} />}
    >
      {children}
    </Suspense>
  );
}

function page(element: ReactNode) {
  return <SuspensePage>{element}</SuspensePage>;
}

/**
 * 注册无斜杠与尾斜杠两条 OSS 路径。
 * 壳外路由包 Suspense；已登录路由传 suspense=false，冒泡到壳层那一次 fallback。
 */
function dual(
  path: string,
  element: ReactNode,
  suspense = true,
): RouteObject[] {
  const node = suspense ? page(element) : element;
  const base = path.replace(/\/$/, "").replace(/^\//, "");
  return [
    { path: base, element: node },
    { path: `${base}/`, element: node },
  ];
}

function buildRoutes(includeMock: boolean): RouteObject[] {
  const routes: RouteObject[] = [
    {
      element: <AuthenticatedLayout />,
      children: [
        {
          element: <WorkspaceShellLayout />,
          children: [
            { index: true, element: <WorkspaceHomePage /> },
            ...dual("dashboard", <DashboardRedirectPage />, false),
            ...dual("workspace/campaigns", <CampaignsPage />, false),
            ...dual(
              "workspace/advertiser-accounts",
              <AdvertiserAccountsPage />,
              false,
            ),
            ...dual("workspace/stores", <StoresPage />, false),
            ...dual("workspace/creative-assets", <CreativeAssetsPage />, false),
            ...dual("workspace/business-centers", <BusinessCentersPage />, false),
            ...dual("workspace/integrations", <IntegrationsPage />, false),
            ...dual("workspace/referrals", <ReferralsPage />, false),
            ...dual("workspace/members", <MembersPage />, false),
            ...dual("workspace/roles", <RolesPage />, false),
            ...dual("workspace/audit-events", <AuditEventsPage />, false),
            ...dual(
              "workspace/profit-calculator",
              <ProfitCalculatorPage />,
              false,
            ),
            ...dual("workspace/roi-calculator", <RoiCalculatorPage />, false),
            ...dual("workspace/audiences", <AudiencesPage />, false),
            ...dual("workspace/paused", <PausedPage />, false),
          ],
        },
        ...dual("settings", <SettingsPage />, false),
        ...dual("admin/users", <AdminUsersPage />, false),
        ...dual("admin/workspaces", <AdminWorkspacesPage />, false),
        ...dual("admin/audit-events", <AdminAuditEventsPage />, false),
      ],
    },
    ...dual("onboarding", <OnboardingPage />),
    ...dual("auth/login", <LoginPage />),
    ...dual("auth/register", <RegisterPage />),
    ...dual("auth/forgot-password", <ForgotPasswordPage />),
    ...dual("auth/update-password", <UpdatePasswordPage />),
    ...dual("auth/confirm", <ConfirmPage />),
    ...dual("auth/callback", <AuthCallbackPage />),
    ...dual("auth/invitation", <InvitationPage />),
    ...dual(
      "functions/v1/tiktok-integration/oauth/callback",
      <TikTokOAuthCallbackPage />,
    ),
    {
      path: "*",
      element: <Navigate to={toStaticExportHref("/")} replace />,
    },
  ];

  if (includeMock) {
    const MockPage = lazy(() => import("@/pages/mock/tiktok-bulk"));
    routes.splice(-1, 0, ...dual("mock/tiktok-bulk", <MockPage />));
  }

  return routes;
}

/** Browser router for the Vite SPA (trailing-slash and slash-free paths both registered). */
export function createAppRouter() {
  return createBrowserRouter([
    {
      element: <RootLayout />,
      children: buildRoutes(import.meta.env.DEV),
    },
  ]);
}

export { buildRoutes };
